/**
 * FRONT-1099: pure copy-builder for the Memory entity-detail delete confirm.
 * Deleting an entity is a recoverable trash (backend cascades its
 * relationships into the same undo batch), but the confirm dialog used to
 * say nothing about the connections going with it — this fills that gap
 * with a count-aware warning, plus a merge nudge for likely duplicates.
 *
 * Extracted out of `entity-detail.tsx` (a heavy component file) into its own
 * plain module so it's testable in plain node without dragging in the UI
 * component tree — same pure-fn/thin-component split as
 * `use-work-selection.ts`'s `buildBulkDeleteConfirmCopy`.
 */

export interface DeleteEntityConfirmCopy {
  title: string;
  description: string;
}

const RESTORE_HINT = 'You can undo this, or restore it later from the Recycle bin.';

export function buildDeleteEntityConfirmCopy(
  name: string,
  relationshipCount: number | undefined,
): DeleteEntityConfirmCopy {
  const label = name || 'this item';
  const title = `Delete "${label}"?`;

  // Count unknown (detail query still in flight) — warn without a number
  // rather than silently implying there are no connections.
  if (relationshipCount === undefined) {
    return {
      title,
      description:
        `Its connections to other entities move with it — if this is a duplicate, ` +
        `merging is the safer move. ${RESTORE_HINT}`,
    };
  }

  if (relationshipCount <= 0) {
    return { title, description: RESTORE_HINT };
  }

  const noun = relationshipCount === 1 ? 'connection' : 'connections';
  return {
    title,
    description:
      `This also removes its ${relationshipCount} ${noun} to other entities — if this is a duplicate, ` +
      `merging is the safer move. ${RESTORE_HINT}`,
  };
}
